import {
  TRACK_MATCH_STATUS_LINKED,
  TRACK_MATCH_STATUS_LATER,
} from '@/constants/trackMatchStatus';
import { queuedTrackMatchStatusIdForMarkSave } from '@/trackMatching/trackMatchMarkAction';
import { isLinkQueuedForSource, isIsrcQueued } from '@/trackMatching/trackMatchingQueueState';
import type { TrackMatchingAction, TrackMatchingRowState } from '@/trackMatching/trackMatchingTypes';
import { SOURCE_TYPES } from '@/trackMatching/trackMatchingTypes';
import { parseRawDdexRowId } from '@/trackMatching/rawDdexRowId';

/** Metadata copied from a Raw DDEX row when the user queues Link. */
export const LINK_METADATA_FIELD_PATHS = [
  'bitrate',
  'duration',
  'catalog_code',
  'name',
  'remix_name',
  'album.display_string',
  'album.upc',
  'isrc',
] as const;

/**
 * LiveTrack fields whose Link-time write is gated by a BE governance rule
 * (see LiveTrackDdexFieldGovernance::GOVERNED_FIELDS in tabasco-cc). Anything not in this set is
 * a convenience field the FE chooses to ship at link time and the BE applies as-is.
 *
 * Mirror of the user-facing subset of the BE GOVERNED_FIELDS: the 4 metadata fields below match
 * the BE 1:1; the 3 snapshot fields (raw_ddex_record_label_name, raw_ddex_root_record_label_name,
 * raw_ddex_root_record_label_party_id) are intentionally excluded because they are system-managed
 * and have no user-facing cell to highlight.
 *
 * Drift contract: any change to this set (or to the per-field shouldWrite rules in
 * `shouldQueueDdexFieldForLinkUpdate`) must be propagated from the BE governance class in the
 * same PR. Changing only one side will produce silently-wrong yellow highlights and queued payloads.
 */
const BE_GOVERNED_LINK_FIELDS = new Set([
  'name',
  'remix_name',
  'isrc',
  'catalog_code',
]);

function normalizeStringValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Mirror of the BE `remix_name` shouldWrite predicate in
 * `LiveTrackDdexFieldGovernance::ruleTable()` (tabasco-cc). Both sides perform a case-insensitive
 * substring check for "clean"; the two implementations must match exactly. If liveTrackData is
 * undefined (no matchData hydrated for the live row yet), this returns false — i.e. we default
 * to "no Clean guard, allow the queue/highlight" rather than suppressing on incomplete state.
 */
function liveRemixHasCleanGuard(liveTrackData: Record<string, unknown> | undefined): boolean {
  const liveRemix = normalizeStringValue(liveTrackData?.remix_name);
  return liveRemix !== null && liveRemix.toLowerCase().includes('clean');
}

/**
 * Mirrors the BE shouldWrite predicate in LiveTrackDdexFieldGovernance for the four governed
 * metadata fields. Returning false here means: the BE governance rule would suppress this write,
 * so the FE should neither queue the field in update_fields nor light up its yellow indicator.
 *
 * Non-governed fields (bitrate, duration, album.*) always return true — they're convenience
 * fields shipped at link time only and the BE simply applies whatever is in update_fields.
 */
export function shouldQueueDdexFieldForLinkUpdate(
  field: string,
  ddexValue: unknown,
  liveTrackData: Record<string, unknown> | undefined
): boolean {
  if (!BE_GOVERNED_LINK_FIELDS.has(field)) {
    return true;
  }

  switch (field) {
    case 'name':
      return true;
    case 'remix_name':
      return !liveRemixHasCleanGuard(liveTrackData);
    case 'isrc':
    case 'catalog_code':
      return normalizeStringValue(ddexValue) !== null;
    default:
      return true;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function transformFieldName(fieldName: string): string {
  if (fieldName.endsWith('.display_string')) {
    return fieldName.replace('.display_string', '.name');
  }
  return fieldName;
}

function getNestedValue(obj: Record<string, unknown> | undefined, path: string): unknown {
  if (!obj) return undefined;
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
  }, obj as unknown);
}

function transformToNestedObject(flatObj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, Record<string, unknown> | unknown> = {};

  Object.entries(flatObj).forEach(([key, value]) => {
    const parts = key.split('.');
    let current = result;

    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        current[part] = value;
      } else {
        if (!current[part] || typeof current[part] !== 'object') {
          current[part] = {};
        }
        current = current[part] as Record<string, Record<string, unknown> | unknown>;
      }
    });
  });

  return result;
}

export function findRawDdexRowStateByLinkedId(
  states: TrackMatchingRowState[],
  liveTrackId: string | number,
  rawDdexTrackId: unknown
): TrackMatchingRowState | undefined {
  const linkedId = Number(rawDdexTrackId);
  if (!Number.isFinite(linkedId)) {
    return undefined;
  }
  return states.find((rowState) => {
    if (rowState.live_track_id !== liveTrackId || rowState.source !== SOURCE_TYPES.RAW_DDEX) {
      return false;
    }
    return parseRawDdexRowId(rowState.sourceId) === linkedId;
  });
}

function buildFlatMetadataFromMatchRow(
  matchData: Record<string, unknown>,
  fieldPaths: readonly string[],
  liveTrackMatchData: Record<string, unknown> | undefined
): Record<string, unknown> {
  const flat: Record<string, unknown> = {};

  for (const fieldPath of fieldPaths) {
    const fieldValue = getNestedValue(matchData, fieldPath);
    if (fieldValue === undefined) {
      continue;
    }
    if (!shouldQueueDdexFieldForLinkUpdate(fieldPath, fieldValue, liveTrackMatchData)) {
      continue;
    }
    const transformedFieldName = transformFieldName(fieldPath);
    flat[transformedFieldName] = fieldValue;

    const entityType = transformedFieldName.split('.')[0];
    const idField = `${entityType}_id`;
    if (liveTrackMatchData && liveTrackMatchData[idField] !== undefined) {
      flat[`${entityType}.id`] = liveTrackMatchData[idField];
    }
  }

  return flat;
}

function buildLiveTrackBackup(matchData: Record<string, unknown>): Record<string, unknown> {
  const backup: Record<string, unknown> = {};

  for (const [field, originalValue] of Object.entries(matchData)) {
    if (originalValue === undefined) {
      continue;
    }
    if (field === 'album' && isPlainRecord(originalValue)) {
      const albumData = originalValue;
      if (albumData.upc !== undefined) {
        backup['album.upc'] = albumData.upc;
      }
      if (albumData.display_string !== undefined) {
        backup['album.name'] = albumData.display_string;
      }
      if (matchData.album_id !== undefined) {
        backup['album.id'] = matchData.album_id;
      }
    } else {
      backup[transformFieldName(field)] = originalValue;
    }
  }

  return transformToNestedObject(backup);
}

function createEmptyAction(
  liveTrackId: string | number,
  liveTrackMatchData: Record<string, unknown> | undefined
): TrackMatchingAction {
  return {
    live_track_id: liveTrackId,
    source: 'track_matching',
    live_track_code:
      (liveTrackMatchData?.live_track_code as string) ||
      (liveTrackMatchData?.code as string) ||
      '',
    update_audio_file: null,
    update_cover_art: null,
    update_fields: null,
    raw_ddex_track_id: undefined,
    live_track_backup: null,
  };
}

function patchUpdateFields(
  existing: Record<string, unknown> | null,
  incoming: Record<string, unknown>
): Record<string, unknown> {
  const merged = { ...(existing ?? {}) };
  for (const [key, value] of Object.entries(incoming)) {
    if (isPlainRecord(value) && isPlainRecord(merged[key])) {
      merged[key] = { ...(merged[key] as Record<string, unknown>), ...value };
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

function applyLinkToAction(
  action: TrackMatchingAction,
  linkRow: TrackMatchingRowState,
  liveTrackMatchData: Record<string, unknown> | undefined
): void {
  const rawDdexId = parseRawDdexRowId(linkRow.sourceId);
  action.raw_ddex_track_id = rawDdexId ?? undefined;

  if (!linkRow.matchData) {
    if (rawDdexId != null) {
      action.update_fields = transformToNestedObject({
        raw_ddex_track_id: rawDdexId,
        track_match_status_id: TRACK_MATCH_STATUS_LINKED,
      });
    }
    return;
  }

  const trackFilePath = linkRow.matchData.track_file_path;
  if (typeof trackFilePath === 'string' && trackFilePath) {
    action.update_audio_file = trackFilePath;
  }

  const coverArtPath = linkRow.matchData.cover_art_file_path;
  if (typeof coverArtPath === 'string' && coverArtPath) {
    action.update_cover_art = coverArtPath;
  }

  const flatFields = buildFlatMetadataFromMatchRow(
    linkRow.matchData,
    LINK_METADATA_FIELD_PATHS,
    liveTrackMatchData
  );
  flatFields.raw_ddex_track_id = rawDdexId;
  flatFields.track_match_status_id = TRACK_MATCH_STATUS_LINKED;

  action.update_fields = transformToNestedObject(flatFields);
}

function applyIsrcToAction(
  action: TrackMatchingAction,
  isrcRow: TrackMatchingRowState,
  persistedStatusId: number | undefined
): void {
  if (!isrcRow.matchData?.isrc) {
    return;
  }

  // SAU-175: persistedStatusId is no longer inspected on ISRC apply — the BE listener
  // resets LATER (and any non-LINKED) track to NOT_RUN before re-running matching, and the
  // pipeline auto-revives the row to HAS_MATCHES if the new ISRC produces a hit.
  void persistedStatusId;

  const flat: Record<string, unknown> = {
    isrc: isrcRow.matchData.isrc,
  };
  action.update_fields = transformToNestedObject(flat);
}

/**
 * SAU-184: queue an artist-name cleanup. The BE applies a nested `artist` payload by renaming the
 * Artist row the live track already points at (TrackFileService::updateRelatedModel resolves it via
 * live_track.artist_id), so the FE only ships the cleaner name — no artist_id, no resolution. Merged
 * (not overwritten) so it composes with a queued Link / Apply ISRC on the same save.
 */
function applyArtistToAction(action: TrackMatchingAction, name: string): void {
  action.update_fields = patchUpdateFields(action.update_fields, { artist: { name } });
}

function applyMarkToAction(
  action: TrackMatchingAction,
  persistedStatusId: number | undefined,
  liveTrackMatchData: Record<string, unknown> | undefined
): void {
  action.update_fields = transformToNestedObject({
    track_match_status_id: queuedTrackMatchStatusIdForMarkSave(persistedStatusId, liveTrackMatchData),
  });
}

function applyUnlinkResolution(
  action: TrackMatchingAction,
  liveTrackRow: TrackMatchingRowState,
  rowStates: TrackMatchingRowState[],
  linkRow: TrackMatchingRowState | undefined
): void {
  const previouslyLinkedId = liveTrackRow.matchData?.raw_ddex_track_id;
  if (previouslyLinkedId == null || previouslyLinkedId === '') {
    return;
  }

  const previouslyLinkedRow = findRawDdexRowStateByLinkedId(
    rowStates,
    liveTrackRow.live_track_id,
    previouslyLinkedId
  );

  const anyLinkQueued = liveTrackRow.pendingPrimarySave?.kind === 'link';

  if (!anyLinkQueued) {
    action.raw_ddex_track_id = null;
    const flat: Record<string, unknown> = { raw_ddex_track_id: null };
    if (!liveTrackRow.markQueued) {
      flat.track_match_status_id = TRACK_MATCH_STATUS_LATER;
    }
    action.update_fields = patchUpdateFields(action.update_fields, flat);
    return;
  }

  if (
    previouslyLinkedRow &&
    isLinkQueuedForSource(liveTrackRow, previouslyLinkedRow.sourceId)
  ) {
    action.raw_ddex_track_id = previouslyLinkedId as number;
    action.update_audio_file = null;
    action.update_cover_art = null;

    if (action.update_fields) {
      const filtered: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(action.update_fields)) {
        if (
          key !== 'bitrate' &&
          key !== 'duration' &&
          !key.includes('cover') &&
          !key.includes('audio')
        ) {
          filtered[key] = value;
        }
      }
      action.update_fields = filtered;
    }
    return;
  }

  if (linkRow) {
    const selectedId = parseRawDdexRowId(linkRow.sourceId);
    action.raw_ddex_track_id = selectedId ?? undefined;
  }
}

export function isFieldHighlightedForRow(
  rowData: Record<string, unknown> & { source: string; id: string | number },
  field: string,
  liveTrackRow: TrackMatchingRowState | undefined
): boolean {
  if (!liveTrackRow) {
    return false;
  }
  const pending = liveTrackRow.pendingPrimarySave;
  if (pending?.kind === 'isrc' && rowData.source === SOURCE_TYPES.SPOTIFY && field === 'isrc') {
    return true;
  }
  // SAU-184: a queued artist clean lights up the live track's own artist cell so the user sees
  // which row will change. The clicked source cell shows its selected state in TrackMatchingArtistCell.
  // The LiveTrack artist column's key is `artist.display_string` (models.json _filterFieldMappings);
  // accept the bare `artist` too in case the mapping is dropped.
  if (
    liveTrackRow.pendingArtistRename &&
    rowData.source === SOURCE_TYPES.LIVE_TRACK &&
    (field === 'artist' || field === 'artist.display_string')
  ) {
    return true;
  }
  if (
    pending?.kind === 'link' &&
    rowData.source === SOURCE_TYPES.RAW_DDEX &&
    pending.rawDdexSourceId === rowData.id &&
    (LINK_METADATA_FIELD_PATHS as readonly string[]).includes(field)
  ) {
    // Mirror BE governance shouldWrite rules so yellow only lights up on fields whose values will
    // actually be written by the Link action. The DDEX source value comes from rowData; the live
    // remix (for the "Clean" guard) comes from liveTrackRow.matchData.
    const ddexValue = getNestedValue(rowData, field);
    return shouldQueueDdexFieldForLinkUpdate(field, ddexValue, liveTrackRow.matchData);
  }
  return false;
}

function findQueuedLinkRow(
  liveTrackId: string | number,
  rowStates: TrackMatchingRowState[],
  liveTrackRow: TrackMatchingRowState | undefined
): TrackMatchingRowState | undefined {
  const pending = liveTrackRow?.pendingPrimarySave;
  if (pending?.kind !== 'link') {
    return undefined;
  }
  return rowStates.find(
    (rowState) =>
      rowState.live_track_id === liveTrackId &&
      rowState.source === SOURCE_TYPES.RAW_DDEX &&
      rowState.sourceId === pending.rawDdexSourceId
  );
}

function findQueuedIsrcRow(
  liveTrackId: string | number,
  rowStates: TrackMatchingRowState[]
): TrackMatchingRowState | undefined {
  const liveTrackRow = rowStates.find(
    (rowState) =>
      rowState.live_track_id === liveTrackId &&
      rowState.source === SOURCE_TYPES.LIVE_TRACK &&
      rowState.sourceId === liveTrackId
  );
  if (!isIsrcQueued(liveTrackRow)) {
    return undefined;
  }
  return rowStates.find(
    (rowState) =>
      rowState.live_track_id === liveTrackId && rowState.source === SOURCE_TYPES.SPOTIFY
  );
}

export function buildTrackMatchingActionForLiveTrack(
  liveTrackId: string | number,
  rowStates: TrackMatchingRowState[],
  persistedStatusId: number | undefined
): TrackMatchingAction | null {
  const liveTrackRow = rowStates.find(
    (rowState) =>
      rowState.live_track_id === liveTrackId &&
      rowState.source === SOURCE_TYPES.LIVE_TRACK &&
      rowState.sourceId === liveTrackId
  );
  const linkRow = findQueuedLinkRow(liveTrackId, rowStates, liveTrackRow);
  const isrcRow = findQueuedIsrcRow(liveTrackId, rowStates);

  const artistRename = liveTrackRow?.pendingArtistRename ?? null;

  if (
    !linkRow &&
    !isrcRow &&
    !artistRename &&
    !liveTrackRow?.unlinkQueued &&
    !liveTrackRow?.markQueued
  ) {
    return null;
  }

  const action = createEmptyAction(liveTrackId, liveTrackRow?.matchData);

  if (linkRow) {
    applyLinkToAction(action, linkRow, liveTrackRow?.matchData);
  } else if (isrcRow) {
    applyIsrcToAction(action, isrcRow, persistedStatusId);
  } else if (liveTrackRow?.markQueued) {
    applyMarkToAction(action, persistedStatusId, liveTrackRow?.matchData);
  }

  if (liveTrackRow?.unlinkQueued) {
    applyUnlinkResolution(action, liveTrackRow, rowStates, linkRow);
  }

  // Standalone artist clean — apply last so it merges on top of any Link / ISRC update_fields.
  if (artistRename) {
    applyArtistToAction(action, artistRename.name);
  }

  if (liveTrackRow?.matchData) {
    action.live_track_backup = buildLiveTrackBackup(liveTrackRow.matchData);
  }

  return action;
}
